import { DocumentDownloadIcon, GiftIcon, PlaceholderPhotoIcon } from '@fourthwall/icons'
import { getCurrencySymbol } from '@fourthwall/utils/dist/common'
import isUndefined from 'lodash-es/isUndefined'
import React from 'react'
import { PriceString } from '../../../functional/PriceString'
import { Banner } from '../../Banner'
import { ButtonClean } from '../../ButtonClean'
import { Icon } from '../../Icon'
import { Tag } from '../../Tag'
import * as S from './styled'
import type { ProductsListItemProps } from './types'

const placeholderHeightMap: Record<NonNullable<ProductsListItemProps['size']>, number> = {
  tiny: 12,
  small: 16,
  medium: 24,
  large: 24,
}

const renderThumbnail = ({
  type,
  name,
  image,
  price,
  size,
}: Pick<ProductsListItemProps, 'type' | 'name' | 'image' | 'price' | 'size'>) => {
  if (type === 'donation' && price) {
    return (
      <S.Donation aria-label={name} data-testid="ProductListItem.Donation">
        {getCurrencySymbol(price.currency)}
      </S.Donation>
    )
  }

  if (!image) {
    const placeholderHeight = placeholderHeightMap[size!]

    if (type === 'giftCard') {
      return (
        <S.Placeholder>
          <Icon component={GiftIcon} height={placeholderHeight} />
        </S.Placeholder>
      )
    }

    return (
      <S.Placeholder>
        <Icon component={PlaceholderPhotoIcon} height={placeholderHeight} />
      </S.Placeholder>
    )
  }

  return <img src={image} alt={name} />
}

export const ProductsListItem = ({
  type = 'product',
  image,
  name,
  description,
  tag,
  banner,
  downloads,
  quantity,
  price,
  details,
  size = 'medium',
  footer,
  onFileDownload,
}: ProductsListItemProps) => {
  return (
    <S.Container $size={size}>
      <S.Row>
        <S.ImageContainer $size={size}>
          {renderThumbnail({ type, image, name, price, size })}
        </S.ImageContainer>
        <S.Content>
          <S.Header>
            <S.Name>{name}</S.Name>
            {!isUndefined(quantity) && <S.Quantity>{quantity}</S.Quantity>}
            {!isUndefined(price) && (
              <S.Price>
                <PriceString value={price.value} currency={price.currency} />
              </S.Price>
            )}
          </S.Header>
          <S.DescriptionWrapper>
            {description && <S.Description>{description}</S.Description>}
            {details && <S.Details>{details}</S.Details>}
            {tag && (
              <S.TagWrapper>
                <Tag label={tag} />
              </S.TagWrapper>
            )}
            {banner && (
              <Banner appearance="inherit" size="small">
                {banner}
              </Banner>
            )}
            {downloads && downloads.length > 0 && (
              <S.Downloads data-testid="ProductsListItem.Downloads">
                {downloads.map(({ name, fileSize, uri }) => {
                  const itemName = `${name} (${fileSize})`

                  return (
                    <S.DownloadsItem key={uri}>
                      <S.DownloadsItemName title={itemName}>
                        <S.DownloadsItemNameIcon>
                          <Icon component={DocumentDownloadIcon} height={20} />
                        </S.DownloadsItemNameIcon>
                        <S.DownloadsItemNameText>{itemName}</S.DownloadsItemNameText>
                      </S.DownloadsItemName>
                      {(onFileDownload || uri) && (
                        <S.DownloadsItemDownload>
                          <ButtonClean
                            label="Download"
                            href={onFileDownload ? undefined : uri}
                            title={`Download ${itemName}`}
                            onClick={onFileDownload && uri ? () => onFileDownload(uri) : undefined}
                          />
                        </S.DownloadsItemDownload>
                      )}
                    </S.DownloadsItem>
                  )
                })}
              </S.Downloads>
            )}
          </S.DescriptionWrapper>
        </S.Content>
      </S.Row>
      {footer || null}
    </S.Container>
  )
}
